import { orgAPI } from "@/api/api";
import { fileRecordKeys } from "@/api/fileRecords";
import { trackFileArchived } from "@/utils/analytics";
import type { FileRecord } from "@cohesive/api-client";
import type { QueryClient } from "@tanstack/react-query";
import type { Node } from "@xyflow/react";
import toast from "react-hot-toast";

/**
 * The two-meaning board delete (D2), shared by every path that removes
 * file-backed nodes — keyboard delete, the multi-select toolbar, and any
 * future gesture. Removing a node must ALWAYS resolve what happens to the
 * backing row:
 *
 * - the node's entity id is matched against the canvas's file rows — BY
 *   TARGET id too, because a promoted file's node keeps the workspace target
 *   id while the canvas row is the link (reference) row;
 * - a resolved LINK row is deleted → the reference is removed from this
 *   canvas, its workspace target untouched;
 * - a resolved canvas FILE row is deleted → the file archives;
 * - an id that resolves to NO canvas row is skipped — it is not a member of
 *   this canvas (e.g. a legacy node pointing straight at a workspace file),
 *   and deleting it blind would archive a workspace file by accident.
 *
 * The invariant this buys: a removed node never leaves its row behind as a
 * drawer orphan, and removing a reference can never archive its target.
 */

/** Entity ids carried by the file-backed nodes in a selection. */
export function collectFileEntityIds(nodes: Node[]): string[] {
  const ids: string[] = [];
  for (const n of nodes) {
    const data = n.data as
      | { entity?: { type?: string; data?: { id?: string } } }
      | undefined;
    const id =
      data?.entity?.type === "file_record" ? data.entity.data?.id : undefined;
    if (id) ids.push(id);
  }
  return ids;
}

export async function deleteFileRowsForRemovedNodes(params: {
  queryClient: QueryClient;
  canvasId: string;
  entityIds: string[];
}): Promise<void> {
  const { queryClient, canvasId, entityIds } = params;
  if (!canvasId || entityIds.length === 0) return;

  let rows =
    queryClient.getQueryData<FileRecord[]>(fileRecordKeys.canvas(canvasId)) ??
    [];
  if (rows.length === 0) {
    // Cache cold (fresh load, another tab) — resolve against the server so
    // the delete can never fall back to guessing ids.
    try {
      const listed = await orgAPI.fileRecords.list({
        class: "canvas",
        canvas_id: canvasId,
      });
      rows = listed;
    } catch {
      rows = [];
    }
  }

  const byId = new Map(rows.map((r) => [r.id, r]));
  const byTarget = new Map<string, FileRecord>();
  for (const r of rows) {
    if (r.target_file_record_id && !byTarget.has(r.target_file_record_id)) {
      byTarget.set(r.target_file_record_id, r);
    }
  }

  const resolved = new Map<string, FileRecord>();
  for (const id of entityIds) {
    const record = byId.get(id) ?? byTarget.get(id);
    if (record) resolved.set(record.id, record);
  }
  if (resolved.size === 0) return;

  const targets = [...resolved.values()];
  const settled = await Promise.allSettled(
    targets.map((r) => orgAPI.fileRecords.delete(r.id)),
  );
  void queryClient.invalidateQueries({
    queryKey: fileRecordKeys.canvas(canvasId),
  });

  const deleted = settled.filter((s) => s.status === "fulfilled").length;
  if (deleted > 0) {
    trackFileArchived({ objectKind: "file", cascade: true, count: deleted });
  }

  const links = targets.filter((r) => r.kind === "link").length;
  const files = targets.length - links;
  if (files > 0) {
    toast.success(
      files === 1
        ? "Moved 1 file to Archive"
        : `Moved ${files} files to Archive`,
    );
  }
  if (links > 0) {
    toast.success(
      links === 1
        ? "Removed from canvas — still in the workspace"
        : `Removed ${links} from canvas — still in the workspace`,
    );
  }
}
